import React, { useState, useEffect } from 'react'
import { registerUserProgressive } from '../lib/supabase'
import { validatePasswordStrength } from '../lib/passwordSecurity'
import { realtimeValidation, ValidationResult } from '../lib/realtimeValidation'
import { ValidationInput } from './ValidationFeedback'
import { ReferralAutocomplete } from './ReferralAutocomplete'
import { API_BASE_URL } from '../constants'
import { applyReferralToRegistration, clearReferralData } from '../lib/referralPersistence'
import {validateStrictEmail, FIELD_LIMITS} from '../lib/userInputValidation'
import { KycRequiredNotice } from './kyc/KycRequiredNotice'

interface EmailRegistrationFormProps {
  onRegistrationSuccess: (user: any) => void
  onSwitchToLogin: () => void
  onSwitchToTelegram?: () => void
  userType?: 'shareholder' | 'affiliate' // New prop to determine registration type
  defaultSponsor?: string // Add defaultSponsor prop
}

// Progressive form steps
type RegistrationStep = 'email' | 'username' | 'complete'

export const EmailRegistrationFormProgressive: React.FC<EmailRegistrationFormProps> = ({
  onRegistrationSuccess,
  onSwitchToLogin,
  userType = 'affiliate', // Default to affiliate for backward compatibility
  defaultSponsor
}) => {
  // Progressive form state
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('email')
  const [registrationMode] = useState<'new' | 'telegram'>('new')
  const [generalError, setGeneralError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Simplified form data (removed phone, country, telegram, fullName fields)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    sponsorUsername: defaultSponsor || '',
    campaignSource: ''
  })

  // New state for sponsor assignment flow
  const [_hasSponsor, setHasSponsor] = useState<boolean | undefined>(undefined)
  const [, setShowSponsorQuestion] = useState(false) // Changed to false - always require sponsor username

  // Email verification state
  const [emailVerification, setEmailVerification] = useState({
    isVerified: false,
    isModalOpen: false,
    tempUserId: null as number | null,
    isEmailValid: false,
    verificationCode: undefined as string | undefined,
    codeExpiry: undefined as number | undefined
  })

  // Real-time validation states
  const [emailValidation, setEmailValidation] = useState<ValidationResult | null>(null)
  const [usernameValidation, setUsernameValidation] = useState<ValidationResult | null>(null)

  // Password visibility states (default to visible)
  const [showPassword, setShowPassword] = useState(true)
  const [showConfirmPassword, setShowConfirmPassword] = useState(true)
  const [sponsorValidation, setSponsorValidation] = useState<ValidationResult | null>(null)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Test account warning state
  const [showTestAccountWarning, setShowTestAccountWarning] = useState(false)
  const [showSignupKycNotice, setShowSignupKycNotice] = useState(() => {
    try {
      return sessionStorage.getItem('aureus_kyc_signup_notice_dismissed') !== '1'
    } catch {
      return true
    }
  })

  // Helper function to normalize Gmail addresses (removes +alias)
  const normalizeGmailAddress = (email: string): string => {
    const trimmedEmail = email.toLowerCase().trim()
    const [localPart, domain] = trimmedEmail.split('@')

    if (domain === 'gmail.com') {
      // Remove everything after + sign in Gmail addresses
      const baseLocal = localPart.split('+')[0]
      return `${baseLocal}@${domain}`
    }

    return trimmedEmail
  }

  // Check if email is a test email (my personal email or any Gmail alias)
  const isTestEmail = (email: string): boolean => {
    const normalized = normalizeGmailAddress(email)
    return normalized === 'jp.rademeyer84@gmail.com'
  }

  // Update test account warning based on sponsor and email
  useEffect(() => {
    const sponsorUsername = formData.sponsorUsername.toLowerCase().trim()
    const emailValue = formData.email.trim()

    // Show warning if:
    // 1. Sponsor is jprademeyer84 (my test account)
    // 2. Email is NOT my test email
    const shouldShowWarning = !!(sponsorUsername === 'jprademeyer84' && emailValue && !isTestEmail(emailValue))

    setShowTestAccountWarning(shouldShowWarning)
  }, [formData.sponsorUsername, formData.email])

  // Validate default sponsor on component mount and handle sponsor question logic
  useEffect(() => {
    if (defaultSponsor && userType === 'affiliate') {
      // If there's a default sponsor (from referral link), skip the sponsor question
      setHasSponsor(true)
      setShowSponsorQuestion(false)
      realtimeValidation.validateSponsorUsername(defaultSponsor, setSponsorValidation, formData.username)
    } else {
      // For all registration types (including shareholder), always require sponsor username
      setHasSponsor(true)
      setShowSponsorQuestion(false)
    }
  }, [defaultSponsor, userType])

  // Apply stored referral data on component mount.
  // v4.7.61: shareholder path also honors localStorage referral when present, but
  // never overrides a sponsor that is already pre-filled (defaultSponsor prop or
  // earlier manual entry). The user remains free to edit the auto-filled value
  // before submitting — handleInputChange revalidates on every keystroke. This
  // closes the gap where a user landing via a referral link (e.g. aureus.africa/<username>)
  // and then choosing Purchase Shares would silently lose their sponsor and have to
  // re-type it from memory, which historically produced wrong-sponsor assignments.
  useEffect(() => {
    if (userType === 'shareholder') {
      const hasPreFilledSponsor = (formData.sponsorUsername || '').trim().length > 0
      if (hasPreFilledSponsor) {
        console.log('🏢 Shareholder registration — sponsor already provided, skipping localStorage auto-fill')
        setHasSponsor(true)
        setShowSponsorQuestion(false)
        return
      }
      // fall through to apply stored referral (same logic as affiliate path)
    }

    const referralData = applyReferralToRegistration()

    if (referralData.sponsorUsername) {
      console.log('✅ Applying stored referral data:', referralData)

      setFormData(prev => ({
        ...prev,
        sponsorUsername: referralData.sponsorUsername || '',
        campaignSource: referralData.campaignSource || ''
      }))

      // If there's referral data, skip the sponsor question and set hasSponsor to true
      setHasSponsor(true)
      setShowSponsorQuestion(false)

      // Validate the sponsor username if it's for affiliate registration
      if (referralData.sponsorUsername) {
        realtimeValidation.validateSponsorUsername(referralData.sponsorUsername, setSponsorValidation, formData.username)
      }
    } else {
      // No referral data - always require sponsor username for regular registration
      setHasSponsor(true)
      setShowSponsorQuestion(false)
    }
  }, [userType])

  // Handle sponsor question response - REMOVED: No longer needed as sponsor is always required

  // Handle input changes with real-time validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    // v4.4.0: usernames cannot contain spaces — strip them silently from the
    // sponsorUsername input so a stray paste / mobile keyboard auto-space does
    // not produce a "user not found" error against a value that the server
    // would otherwise lookup correctly.
    const sanitizedValue = name === 'sponsorUsername' ? value.replace(/\s+/g, '') : value

    setFormData(prev => ({ ...prev, [name]: sanitizedValue }))

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    // Real-time validation for email. Editing the address cancels a code already accepted.
    if (name === 'email') {
      const emailResult = validateStrictEmail(sanitizedValue)
      realtimeValidation.validateEmail(sanitizedValue, setEmailValidation)
      setEmailVerification(prev => ({
        ...prev,
        isEmailValid: emailResult.valid,
        isVerified: false,
        isModalOpen: false,
        tempUserId: null,
        verificationCode: undefined,
        codeExpiry: undefined
      }))
    }

    // Real-time validation for username
    if (name === 'username') {
      realtimeValidation.validateUsername(sanitizedValue, setUsernameValidation)
    }

    // v4.3.99 — fire AJAX validation for ALL registration types (affiliate + shareholder).
    // The previous `userType !== 'shareholder'` guard skipped the /api/check-duplicates call
    // for shareholder registrations, leaving sponsorValidation=null. validateForm() already
    // requires sponsorValidation.isValid for every userType, so the form was unsubmittable
    // for shareholders even when the sponsor existed (e.g. harish007). Both branches now
    // agree: sponsor is mandatory and must be AJAX-verified before submit.
    // v4.4.0 — uses sanitizedValue (spaces stripped); the validation library
    // debounces for 800ms and only renders "Checking..." after the debounce
    // settles, so partial mid-typing lookups no longer flicker the UI.
    if (name === 'sponsorUsername' && sanitizedValue.trim().length > 0) {
      realtimeValidation.validateSponsorUsername(sanitizedValue.trim(), setSponsorValidation, formData.username)
    } else if (name === 'sponsorUsername') {
      setSponsorValidation(null)
    }
  }

  // Handle email verification
  const handleEmailVerification = async (email: string) => {
    try {
      setGeneralError('')
      console.log('🔍 Starting email verification process for:', email)

      // Same check as the final step, so an invalid address stops here.
      const emailResult = validateStrictEmail(email)
      if (!emailResult.valid) {
        setErrors(prev => ({ ...prev, email: emailResult.error || 'Please enter a valid email address' }))
        setEmailVerification(prev => ({ ...prev, isEmailValid: false }))
        return
      }
      const emailValueRaw = emailResult.sanitized || email.trim().toLowerCase()

      // Check for duplicates
      const resp = await fetch(`${API_BASE_URL}/api/check-duplicates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValueRaw })
      })
      const dup = await resp.json()
      if (resp.ok && dup.emailDuplicate) {
        setErrors(prev => ({ ...prev, email: 'This email address is already registered. Please use a different email or try logging in.' }))
        return
      }

      // Generate verification code
      const verificationCode = Math.floor(100000 + Math.random() * 900000).toString()
      setEmailVerification(prev => ({
        ...prev,
        tempUserId: null,
        isModalOpen: true,
        verificationCode,
        codeExpiry: Date.now() + (15 * 60 * 1000) // 15 minutes
      }))

      // Send verification email
      const response = await fetch(`${API_BASE_URL}/api/send-verification-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailValueRaw,
          code: verificationCode,
          purpose: 'registration',
          userName: 'User',
          expiryMinutes: 15
        }),
      })

      const responseData = await response.json()
      if (!response.ok) {
        console.error('❌ API Error:', responseData)
        setGeneralError(`Failed to send verification email: ${responseData.error || 'Server error'}`)
        return
      }

      console.log('✅ Verification email sent successfully')
    } catch (error) {
      console.error('Email verification error:', error)
      setGeneralError(`Failed to send verification email: ${error instanceof Error ? error.message : 'Network error'}`)
    }
  }

  // Handle email verification success
  const handleEmailVerificationSuccess = () => {
    setEmailVerification(prev => ({
      ...prev,
      isVerified: true,
      isModalOpen: false,
      tempUserId: null,
      verificationCode: undefined,
      codeExpiry: undefined
    }))
    // Advance to username step after email verification
    setCurrentStep('username')
  }

  // Handle username validation completion
  const handleUsernameValidated = () => {
    // Advance to complete step after username is validated
    setCurrentStep('complete')
  }

  const returnToEmailStep = () => {
    setCurrentStep('email')
    setGeneralError('')
    setErrors({})
  }

  // Handle verification code submission
  const handleVerificationCodeSubmit = async (code: string) => {
    if (!emailVerification.verificationCode || !emailVerification.codeExpiry) {
      setGeneralError('No verification code found. Please request a new one.')
      return
    }

    if (Date.now() > emailVerification.codeExpiry) {
      setGeneralError('Verification code has expired. Please request a new one.')
      return
    }

    if (code === emailVerification.verificationCode) {
      handleEmailVerificationSuccess()
    } else {
      setGeneralError('Invalid verification code. Please try again.')
    }
  }

  const handleEmailVerificationClose = () => {
    setEmailVerification(prev => ({
      ...prev,
      isModalOpen: false,
      tempUserId: null,
      verificationCode: undefined,
      codeExpiry: undefined
    }))
  }

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {}

    // Email validation
    const emailResult = validateStrictEmail(formData.email)
    if (!emailResult.valid) {
      newErrors.email = emailResult.error!
    } else if (!emailVerification.isVerified) {
      newErrors.email = 'Please verify your email address first'
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 2 || formData.username.length > 25) {
      newErrors.username = 'Username must be 2-25 characters'
    }



    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else {
      const passwordValidation = validatePasswordStrength(formData.password, formData.username)
      if (!passwordValidation.valid) {
        newErrors.password = passwordValidation.errors[0]
      }
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Sponsor username validation - always required for all registration types
    if (!formData.sponsorUsername.trim()) {
      newErrors.sponsorUsername = 'Please enter your sponsor\'s username'
    } else if (!sponsorValidation || sponsorValidation.isChecking || !sponsorValidation.isValid) {
      newErrors.sponsorUsername = 'Please wait for sponsor validation to complete or enter a valid sponsor username'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setGeneralError('')

    console.log('🟡 [Register] Create Account clicked, running validation…')
    if (!validateForm()) {
      console.warn('🔴 [Register] Validation failed — see field errors above the Create Account button.')
      // Surface a top-level message so the user gets feedback even if a per-field error rendering is missing/cached
      setGeneralError('Please fix the highlighted fields below before continuing.')
      return
    }
    console.log('🟢 [Register] Validation passed, submitting…')

    setIsSubmitting(true)

    try {
      // Register user with simplified progressive registration
      const userData = {
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        username: formData.username.toLowerCase().trim(),
        sponsorUsername: formData.sponsorUsername.toLowerCase().trim(),
        campaignSource: formData.campaignSource || (userType === 'shareholder' ? 'direct-registration' : 'affiliate-registration'),
        userType: userType // Add user type to registration data
      }

      const result = await registerUserProgressive(userData)

      if (result.user && !result.error) {
        // Clear referral data after successful registration
        clearReferralData()

        onRegistrationSuccess(result.user)
      } else {
        // Ensure error is a string, not an object
        const errorMessage = result.error?.message || 'Registration failed. Please try again.'
        setGeneralError(errorMessage)
      }
    } catch (error) {
      setGeneralError('Registration failed. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {showSignupKycNotice && (
        <KycRequiredNotice
          mode="signup"
          onClose={() => {
            try {
              sessionStorage.setItem('aureus_kyc_signup_notice_dismissed', '1')
            } catch {
              /* ignore */
            }
            setShowSignupKycNotice(false)
          }}
        />
      )}
      <div className="registration-form">
      {/* Progress Indicator */}
      <div className="progress-indicator">
        <div
          className={`progress-step ${currentStep === 'email' ? 'active' : currentStep === 'username' || currentStep === 'complete' ? 'completed' : ''}`}
          onClick={() => { if (currentStep !== 'email') returnToEmailStep() }}
          role={currentStep !== 'email' ? 'button' : undefined}
          style={currentStep !== 'email' ? { cursor: 'pointer' } : undefined}
        >
          <div className="progress-step-circle">
            {currentStep === 'username' || currentStep === 'complete' ? '✓' : '1'}
          </div>
          <span className="progress-step-label">Email</span>
        </div>
        <div className={`progress-connector ${currentStep === 'username' || currentStep === 'complete' ? 'completed' : ''}`}></div>
        <div className={`progress-step ${currentStep === 'username' ? 'active' : currentStep === 'complete' ? 'completed' : ''}`}>
          <div className="progress-step-circle">
            {currentStep === 'complete' ? '✓' : '2'}
          </div>
          <span className="progress-step-label">Username</span>
        </div>
        <div className={`progress-connector ${currentStep === 'complete' ? 'completed' : ''}`}></div>
        <div className={`progress-step ${currentStep === 'complete' ? 'active' : ''}`}>
          <div className="progress-step-circle">3</div>
          <span className="progress-step-label">Complete</span>
        </div>
      </div>




        {/* Progressive Form Steps */}
        <form onSubmit={handleSubmit} className="form">
          {/* Step 1: Email Validation */}
          {currentStep === 'email' && (
            <div className="form-section">
              <div className="form-header">
                <h4 className="form-step-title">Step 1: Email Verification</h4>
                <p className="form-step-subtitle">Enter your email address to get started</p>
              </div>

              <div className="form-group">
                <ValidationInput
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  validation={emailValidation}
                  placeholder="Enter your email address"
                  autoComplete="email"
                  className="form-input"
                  label="Email Address"
                  required={true}
                  maxLength={FIELD_LIMITS.email.max}
                />
                {errors.email && (
                  <p className="form-error-text">{errors.email}</p>
                )}

                {/* Send Code Button */}
                {registrationMode === 'new' && emailVerification.isEmailValid && !emailVerification.isVerified && (
                  <div className="form-button-container">
                    <button
                      type="button"
                      onClick={() => handleEmailVerification(formData.email)}
                      className="btn btn-primary"
                      disabled={
                        !emailVerification.isEmailValid ||
                        !!errors.email ||
                        (emailValidation ? (!emailValidation.isValid || emailValidation.isChecking) : false)
                      }
                    >
                      Send Verification Code
                    </button>
                  </div>
                )}

                {registrationMode === 'new' && emailVerification.isEmailValid && !emailVerification.isVerified && (
                  <p className="form-help-text">
                    📧 Click "Send Verification Code" to receive a 6-digit code via email
                  </p>
                )}

                {registrationMode === 'new' && emailVerification.isVerified && (
                  <>
                    <p className="form-success-text">
                      ✅ Email verified successfully! You can now proceed to the next step.
                    </p>
                    <div className="form-button-container">
                      <button
                        type="button"
                        onClick={() => setCurrentStep('username')}
                        className="btn btn-primary"
                      >
                        Continue
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Username Selection */}
          {currentStep === 'username' && (
            <div className="form-section">
              <div className="form-header">
                <h4 className="form-step-title">Step 2: Choose Username</h4>
                <p className="form-step-subtitle">Select a unique username for your account</p>
              </div>

              <div className="form-group">
                <ValidationInput
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  validation={usernameValidation}
                  placeholder="Enter your username"
                  autoComplete="username"
                  className="form-input"
                  label="Username"
                  required={true}
                  maxLength={FIELD_LIMITS.username.max}
                />

                {/* Continue Button */}
                {usernameValidation && usernameValidation.isValid && (
                  <div className="form-button-container">
                    <button
                      type="button"
                      onClick={handleUsernameValidated}
                      className="btn btn-primary"
                    >
                      Continue to Final Step
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Complete Registration */}
          {currentStep === 'complete' && (
            <div className="form-section">
              <div className="form-header">
                <h4 className="form-step-title">Step 3: Complete Registration</h4>
                <p className="form-step-subtitle">Create your password and link to your sponsor</p>
                <button
                  type="button"
                  onClick={returnToEmailStep}
                  className="link-button"
                >
                  Change email
                </button>
              </div>

              {/* Top-level validation summary — always visible when any field fails */}
              {Object.keys(errors).length > 0 && (
                <div
                  role="alert"
                  aria-live="assertive"
                  style={{
                    backgroundColor: '#7f1d1d',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#fecaca'
                  }}
                >
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '14px' }}>
                    ⚠️ Please correct the following before continuing:
                  </p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                    {Object.entries(errors).map(([field, message]) => (
                      <li key={field}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Password */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password <span style={{color: 'var(--error)'}}>*</span>
                </label>
                <div className="password-input-container">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    placeholder="Create a strong password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle-btn"
                  >
                    {showPassword ? (
                      <svg className="password-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 11-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="password-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="form-error-text">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password <span style={{color: 'var(--error)'}}>*</span>
                </label>
                <div className="password-input-container">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="form-input password-input"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="password-toggle-btn"
                  >
                    {showConfirmPassword ? (
                      <svg className="password-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 11-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="password-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="form-error-text">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Sponsor Username Input - Always show for all registration types */}
              {(
                <div className="form-group">
                  <label className="form-label" style={{color: 'var(--gold)', fontWeight: 'bold'}}>
                    Sponsor Username <span style={{color: 'var(--error)'}}>*</span>
                  </label>

                  {/* Test Account Warning */}
                  {showTestAccountWarning && (
                    <div style={{
                      backgroundColor: '#FEF3C7',
                      border: '2px solid #F59E0B',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      <span style={{
                        fontSize: '24px',
                        lineHeight: '1',
                        flexShrink: 0
                      }}>⚠️</span>
                      <div style={{
                        flex: 1
                      }}>
                        <p style={{
                          margin: 0,
                          color: '#92400E',
                          fontSize: '14px',
                          fontWeight: '600',
                          lineHeight: '1.5'
                        }}>
                          This is a test account. Please contact the person who introduced you to Aureus Africa to get their correct referral link.
                        </p>
                      </div>
                    </div>
                  )}

                  {defaultSponsor ? (
                    <div>
                      <div className="sponsor-display">
                        {formData.sponsorUsername}
                        {sponsorValidation && sponsorValidation.isValid && (
                          <span className="sponsor-verified">✓</span>
                        )}
                      </div>
                      {sponsorValidation && sponsorValidation.isValid && (
                        <div className="sponsor-status-success">
                          ✓ {sponsorValidation.sponsorName
                            ? `Sponsor verified: ${sponsorValidation.sponsorName}`
                            : 'Sponsor username found'}
                        </div>
                      )}
                      <p className="form-help-text">
                        You are registering through this sponsor's referral link
                      </p>
                    </div>
                  ) : (
                    <div>
                      <ReferralAutocomplete
                        id="sponsorUsername"
                        name="sponsorUsername"
                        type="text"
                        value={formData.sponsorUsername}
                        onChange={handleInputChange}
                        validation={sponsorValidation}
                        placeholder="Enter your sponsor's username"
                        className="form-input"
                      />
                      {errors.sponsorUsername && (
                        <p className="form-error-text">{errors.sponsorUsername}</p>
                      )}
                      <p className="form-help-text">
                        Enter the username of the person who introduced you to Aureus Alliance Holdings
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="form-button-container">
                <button
                  type="submit"
                  disabled={isSubmitting || showTestAccountWarning}
                  className="btn btn-primary btn-lg"
                  style={{
                    width: '100%',
                    minHeight: '48px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    cursor: (isSubmitting || showTestAccountWarning) ? 'not-allowed' : 'pointer',
                    opacity: showTestAccountWarning ? 0.5 : 1
                  }}
                >
                  {isSubmitting ? (
                    <div className="btn-loading">
                      <div className="loading-spinner"></div>
                      <span>Creating Account...</span>
                    </div>
                  ) : (
                    <span>Create Account</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        {/* General Error Display */}
        {generalError && (
          <div className="alert alert-error">
            <div className="alert-content">
              <span className="alert-icon">⚠️</span>
              <p>{generalError}</p>
            </div>
          </div>
        )}

        {/* Switch to Login */}
        <div className="form-footer">
          <p style={{color: 'var(--text-muted)', marginBottom: 'var(--space-lg)'}}>Already have an account?</p>
          <button
            onClick={onSwitchToLogin}
            className="link-button"
          >
            Sign in instead
          </button>
        </div>

      {/* Email Verification Modal */}
      {emailVerification.isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                📧 Verify Your Email Address
              </h3>
            </div>
            <div className="modal-body">
              <p className="modal-text">
                We've sent a 6-digit verification code to <strong>{formData.email}</strong>.
                Please enter it below to continue with your registration.
              </p>

              {/* Show general error if any */}
              {generalError && (
                <div className="alert alert-error">
                  <div className="alert-content">
                    <span className="alert-icon">⚠️</span>
                    <p>{generalError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={(e) => {
              e.preventDefault()
              const code = (e.target as HTMLFormElement).code.value
              if (code.length === 6) {
                handleVerificationCodeSubmit(code)
              }
            }} className="form">
              <div className="form-group">
                <label className="form-label">
                  Verification Code
                </label>
                <input
                  name="code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  className="form-input verification-code-input"
                  maxLength={6}
                  autoComplete="off"
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    e.target.value = value
                  }}
                />
              </div>

              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={handleEmailVerificationClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleEmailVerification(formData.email)}
                  className="btn btn-outline"
                >
                  Resend
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  Verify
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}


